import React, { Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IconButton, MenuItem, Typography } from '@mui/material';
import Icon from '@mdi/react';
import { mdiDeleteOutline } from '@mdi/js';
import { vars } from '../../../Theme';
import DropdownMenu from '../../General/DropdownMenu/DropdownMenu';
import { useDispatch, useSelector } from 'react-redux';
import {
    ColumnsViewModeType,
    deleteCustomColumnsView,
    setColumnsViewMode,
} from '../../../redux/project/sliceProjectViewData';
import store, { IAppReduxStore } from '../../../redux/store';
import { useParams } from 'react-router-dom';
import { fetchViewData } from '../../../util/ProjectUtils';
import { useEnqueueSnackbar } from '../../../hooks/useEnqueueSnackbar';
import { ToastSeverity } from '../../General/Toast/Toast';

const projectColumnsViewsMenuOptions = [
    {
        label: 'defaultView',
        value: 'defaultView',
    },
    {
        label: 'myView',
        value: 'myView',
    },
];

export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** Get the areaName and viewName from the react router. */
    const params = useParams();

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();
    const enqueueSnackbar = useEnqueueSnackbar();

    const columnsViewMode = useSelector(
        (store: IAppReduxStore) => store.projectViewData.columnsViewMode,
    );
    const fieldsCustom = useSelector(
        (store: IAppReduxStore) => store.projectViewData.fieldsCustom.data,
    );
    const schemaCustom = useSelector(
        (store: IAppReduxStore) => store.projectViewData.schemaCustom.data,
    );

    useEffect(() => {
        if (fieldsCustom.length === 0 && schemaCustom.length === 0) {
            return;
        }
        dispatch(setColumnsViewMode('myView'));
        return () => dispatch(setColumnsViewMode('defaultView'));
    }, [fieldsCustom, schemaCustom]);

    const handleMenuItemChange = (mode: ColumnsViewModeType) => {
        dispatch(setColumnsViewMode(mode));
    };

    return (
        <DropdownMenu
            title={t(`${columnsViewMode}`)}
            sx={{
                fontSize: '14px',
                p: '0 8px',
                height: '34px',
                borderRadius: '5px',
                border: `1px solid ${vars.lightGray}`,
                color: `${vars.font}`,
                '&:hover': {
                    backgroundColor: vars.fillGray,
                },
            }}
            variant='outlined'
        >
            {projectColumnsViewsMenuOptions.map(option => (
                <MenuItem
                    key={option.value}
                    value={option.value}
                    sx={{
                        height: '40px',
                        justifyContent: 'space-between',
                        '& .MuiButtonBase-root': {
                            visibility: 'hidden',
                        },
                        '&:hover': {
                            '& .MuiButtonBase-root': {
                                visibility: 'visible',
                            },
                        },
                    }}
                    onClick={() => handleMenuItemChange(option.value as ColumnsViewModeType)}
                >
                    <Typography>{t(`${option.label}`)}</Typography>
                    {option.value === 'myView' && (
                        <IconButton
                            onClick={async () => {
                                await store.dispatch(
                                    deleteCustomColumnsView({
                                        replicaId: params.replicaId,
                                        viewName: `${params.areaName}.${params.viewName}`,
                                    }),
                                );
                                fetchViewData(params);
                                enqueueSnackbar(t('toastCustomColumnsViewDeleteSuccess'), {
                                    severity: ToastSeverity.Success,
                                });
                            }}
                        >
                            <Icon path={mdiDeleteOutline} size={1} color={vars.font} />
                        </IconButton>
                    )}
                </MenuItem>
            ))}
        </DropdownMenu>
    );
};
